import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgramacionMasivaDetalle } from 'src/app/domain/model/agenda/programacion-masiva';
import { pmBuscarDialogPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';

/**
 * Componente Dumb: Diálogo de búsqueda de asesores
 *
 * Muestra asesores disponibles para seleccionar.
 * NO tiene lógica de negocio ni servicios inyectados.
 */
@Component({
  selector: 'app-pm-buscar-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    TableModule,
    CheckboxModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['../../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      header="Listado de Consultores"
      [visible]="visible"
      (visibleChange)="visibleChange.emit($event)"
      [modal]="true"
      [style]="{ width: '95vw', 'max-width': '60rem' }"
      [breakpoints]="{ '960px': '90vw', '640px': '95vw' }"
      [draggable]="false"
    >
      <div class="text-sm text-gray-600 dark:text-gray-400 mb-2">
        Registros del 1 hasta el {{ asesores.length }} de {{ asesores.length }}
        <span class="ml-4">Página 1 de 1</span>
      </div>

      <div class="md:hidden space-y-3 p-3">
        @if (asesores.length === 0) {
          <div class="text-center text-gray-500 dark:text-gray-400 py-4">
            No se encontraron consultores
          </div>
        }
        @for (asesor of asesores; track $index) {
          <div
            class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
          >
            <div class="flex items-start justify-between gap-2 mb-3">
              <div class="min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Consultor
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [attr.title]="tituloTabla(asesor.nombreAsesor)"
                >
                  {{ textoTabla(asesor.nombreAsesor) }}
                </div>
              </div>
              <p-checkbox
                [(ngModel)]="seleccionados"
                [value]="asesor"
              ></p-checkbox>
            </div>
            <div class="grid grid-cols-1 gap-2 text-sm">
              <div>
                <span class="text-gray-500 dark:text-gray-400">Dni:</span>
                <span class="text-gray-800 dark:text-gray-200">{{
                  textoTabla(asesor.dniAsesor)
                }}</span>
              </div>
            </div>
          </div>
        }
      </div>

      <div class="hidden md:block">
        <div
          role="region"
          [attr.aria-label]="tableConfig.a11y.tableAriaLabel"
          class="sr-only"
        ></div>
        <p-table
          [resizableColumns]="true"
          columnResizeMode="expand"
          [value]="asesores"
          [scrollable]="true"
          [responsiveLayout]="'scroll'"
          [tableStyle]="{
            'min-width': tableConfig.responsive.minWidth || 'max-content'
          }"
          [scrollHeight]="tableConfig.responsive.scrollHeight || '320px'"
          [styleClass]="tableStyleClass"
        >
          <ng-template pTemplate="header">
            <tr>
              <th pResizableColumn>
                <p-checkbox
                  [(ngModel)]="seleccionarTodos"
                  [binary]="true"
                  (onChange)="onToggleSeleccionTodos()"
                >
                </p-checkbox>
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Dni Consultor"
                  >Dni Consultor</span
                >
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Consultor"
                  >Consultor</span
                >
              </th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-asesor>
            <tr>
              <td>
                <p-checkbox
                  [(ngModel)]="seleccionados"
                  [value]="asesor"
                ></p-checkbox>
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(asesor.dniAsesor)"
                  >{{ textoTabla(asesor.dniAsesor) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(asesor.nombreAsesor)"
                  >{{ textoTabla(asesor.nombreAsesor) }}</span
                >
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td
                colspan="3"
                class="text-center text-gray-500 dark:text-gray-400 py-4"
              >
                No se encontraron consultores
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="visibleChange.emit(false)"
          ></p-button>
          <p-button
            label="Agregar"
            icon="pi pi-plus"
            styleClass="min-h-[44px]"
            (onClick)="onAgregar()"
            [disabled]="seleccionados.length === 0"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class PmBuscarDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() asesores: ProgramacionMasivaDetalle[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() agregar = new EventEmitter<ProgramacionMasivaDetalle[]>();

  seleccionados: ProgramacionMasivaDetalle[] = [];
  seleccionarTodos = false;

  ngOnChanges(changes: SimpleChanges): void {
    if ('visible' in changes && !this.visible) {
      this.seleccionados = [];
      this.seleccionarTodos = false;
    }
  }

  readonly tableConfig = pmBuscarDialogPreset.createConfig({
    style: {
      tableClassName: 'tabla-pm-buscar',
      striped: true,
      gridlines: false,
      resizableColumns: true,
      rowHover: false
    },
    responsive: {
      mode: 'hybrid',
      minWidth: 'max-content',
      scrollHeight: '320px',
      cardBreakpointPx: 768
    },
    a11y: {
      tableAriaLabel:
        'Tabla de consultores disponibles para programacion masiva',
      announceSelection: true,
      announcePageChanges: false,
      focusFirstActionOnRowSelect: false
    }
  });

  get tableStyleClass(): string {
    const classes = ['p-datatable-sm', this.tableConfig.style.tableClassName];
    if (this.tableConfig.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (this.tableConfig.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  }

  onToggleSeleccionTodos(): void {
    this.seleccionados = this.seleccionarTodos ? [...this.asesores] : [];
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  onAgregar(): void {
    this.agregar.emit([...this.seleccionados]);
    this.seleccionados = [];
    this.seleccionarTodos = false;
  }
}
